import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states';
import { buildAuthenticatedWsUrl } from './buildAuthenticatedWsUrl';
import { createReconnectingSocket } from './createReconnectingSocket';
import {
  requestDesktopNotificationPermission,
  showInterveneDesktopNotification,
} from '../notifications';

const OrgNotificationsContext = createContext(null);

export function OrgNotificationsProvider({ children }) {
  const userInfo = useRecoilValue(userInformationAtom);
  const organisationId = userInfo?.profile?.organisation?.organisation_id ?? '';
  const userId = userInfo?.id ?? 0;

  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [lastMessage, setLastMessage] = useState(null);
  const [lastV1Event, setLastV1Event] = useState(null);
  const socketRef = useRef(null);
  const subscribersRef = useRef(new Set());

  const notifySubscribers = useCallback((payload) => {
    subscribersRef.current.forEach((fn) => {
      try {
        fn(payload);
      } catch (error) {
        console.error('Org notification subscriber error:', error);
      }
    });
  }, []);

  useEffect(() => {
    const requestPermission = () => {
      requestDesktopNotificationPermission().catch(() => {});
      document.removeEventListener('click', requestPermission);
    };

    document.addEventListener('click', requestPermission);
    return () => document.removeEventListener('click', requestPermission);
  }, []);

  useEffect(() => {
    const wsUrl = buildAuthenticatedWsUrl('/notifications/');
    const socket = createReconnectingSocket(wsUrl, {
      onOpen: () => {
        if (organisationId) {
          socket.send({
            type: 'register',
            organisation_id: organisationId,
            user_id: Number(userId),
          });
        }
      },
    });

    socketRef.current = socket;

    const unsubStatus = socket.on('status', setConnectionStatus);
    const unsubMessage = socket.on('message', (payload) => {
      setLastMessage(payload);
      showInterveneDesktopNotification(payload, userId);
      if (payload?.v === 1) {
        setLastV1Event(payload);
      }
      notifySubscribers(payload);
    });

    return () => {
      unsubStatus();
      unsubMessage();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [organisationId, userId, notifySubscribers]);

  const sendMessage = useCallback((data) => {
    socketRef.current?.send(data);
  }, []);

  const subscribe = useCallback((handler) => {
    subscribersRef.current.add(handler);
    return () => subscribersRef.current.delete(handler);
  }, []);

  const value = useMemo(
    () => ({
      connectionStatus,
      lastMessage,
      lastV1Event,
      sendMessage,
      subscribe,
    }),
    [connectionStatus, lastMessage, lastV1Event, sendMessage, subscribe]
  );

  return (
    <OrgNotificationsContext.Provider value={value}>
      {children}
    </OrgNotificationsContext.Provider>
  );
}

export function useOrgNotificationsSocket() {
  const context = useContext(OrgNotificationsContext);
  if (!context) {
    throw new Error('useOrgNotificationsSocket must be used within OrgNotificationsProvider');
  }
  return context;
}
